import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@connectrpc/connect-query'
import { useState, useMemo } from 'react'
import {
  adminListOrganizations,
  adminListFeatureCatalog,
} from '../../../gen/cortado/v1/cortado-CortadoService_connectquery'
import { OrganizationFeatureEnum } from '../../../gen/cortado/v1/settings_pb'
import { featureLabel, filterableFeatures } from '../../../lib/feature-catalog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table'
import { Input } from '../../../components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'
import {
  SortableHeader,
  compareNumbers,
  compareStrings,
  useTableSort,
} from '../../../components/ui/sortable-header'
import { ChevronRight, Search } from 'lucide-react'

type OrgSortField = 'name' | 'slug' | 'created'

export const Route = createFileRoute('/_admin/organizations/')({
  component: OrganizationsPage,
})

function OrganizationsPage() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [featureFilter, setFeatureFilter] = useState<string>('all')
  const { sortField, sortDirection, toggleSort } = useTableSort<OrgSortField>('name')

  const { data: orgsData, isLoading: orgsLoading } = useQuery(adminListOrganizations, {})
  const { data: catalogData, isLoading: catalogLoading } = useQuery(adminListFeatureCatalog, {})

  const featureOptions = useMemo(
    () => filterableFeatures(catalogData?.entries ?? []),
    [catalogData?.entries],
  )

  const filteredOrgs = useMemo(() => {
    if (!orgsData?.organizations) return []
    let result = orgsData.organizations

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase()
      result = result.filter((org) => org.name.toLowerCase().includes(q))
    }

    if (featureFilter !== 'all') {
      const filterValue = Number(featureFilter) as OrganizationFeatureEnum
      result = result.filter((org) =>
        org.features.some((f) => f.feature === filterValue && f.enabled),
      )
    }

    const dir = sortDirection === 'asc' ? 1 : -1
    return [...result].sort((a, b) => {
      switch (sortField) {
        case 'name':
          return compareStrings(a.name, b.name) * dir
        case 'slug':
          return compareStrings(a.slug, b.slug) * dir
        case 'created':
          return compareNumbers(
            a.createdAt?.seconds ?? 0n,
            b.createdAt?.seconds ?? 0n,
          ) * dir
        default:
          return 0
      }
    })
  }, [orgsData?.organizations, searchQuery, featureFilter, sortField, sortDirection])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Organizations</h1>
        <p className="text-muted-foreground text-sm">
          Manage organizations and feature flags
        </p>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative w-full sm:max-w-sm">
          <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
          <Input
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={featureFilter} onValueChange={setFeatureFilter}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Filter by feature" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All organizations</SelectItem>
            {!catalogLoading &&
              featureOptions.map((f) => (
                <SelectItem key={f} value={String(f)}>
                  {featureLabel(f)}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <SortableHeader
                  active={sortField === 'name'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('name')}
                >
                  Name
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'slug'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('slug')}
                >
                  Slug
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader
                  active={sortField === 'created'}
                  direction={sortDirection}
                  onToggle={() => toggleSort('created')}
                >
                  Created
                </SortableHeader>
              </TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {orgsLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                  Loading organizations...
                </TableCell>
              </TableRow>
            ) : !filteredOrgs.length ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                  {searchQuery.trim() ? 'No organizations match your search' : 'No organizations found'}
                </TableCell>
              </TableRow>
            ) : (
              filteredOrgs.map((org) => (
                <TableRow
                  key={org.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate({ to: '/organizations/$organizationId', params: { organizationId: org.id } })}
                >
                  <TableCell className="font-medium">{org.name}</TableCell>
                  <TableCell>{org.slug || '-'}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {org.createdAt
                      ? new Date(Number(org.createdAt.seconds) * 1000).toLocaleDateString()
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
